import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createToggleRow, safeStorage } from '@learnforge/demo-kit';
import {
  type Equation,
  type SystemResult,
  type Expr,
  solveSystem,
  format,
  canonicalise,
  parse,
  num,
  variable,
  mul,
  add,
} from '../algebra';
import { Fraction } from '@learnforge/demo-math/numeric';
import type { AlgebraContext, AlgebraKey } from '../types';
import {
  createCartesianViewport,
  createFormulaLegend,
  createSvgEl,
  boundaryTextAnchor,
  getCartesianAxisLayout,
  type CartesianDomain,
  type CartesianPlotRect,
  type CartesianViewportHandle,
} from '@learnforge/demo-render';
import { createAugmentedMatrix } from '../render/augmented-matrix';
import {
  createSystem3D,
  type Plane3,
  type System3DHandle,
  type View3D,
  DEFAULT_VIEW,
} from '../render/system-3d';
import { mountActionBar } from '../action-bar';
import { SYSTEM_PRESETS } from '../data/system-presets';
import type { SystemPreset } from '../data/system-presets';
import { niceTickStep } from '../render/parabola-sketch';

// ============================================================================
// Helper: Extract linear coefficients from an expression
// ============================================================================

/**
 * Analyze a single Expr recursively to collect linear coefficients.
 * Returns { coeffs: Fraction[], constant: Fraction } or null if non-linear.
 */
function extractLinearCoefficients(
  expr: Expr,
  vars: readonly string[],
): { coeffs: Fraction[]; constant: Fraction } | null {
  const coeffMap = new Map<string, Fraction>();
  let constant = Fraction.ZERO;

  function collect(e: Expr): Fraction | null {
    switch (e.kind) {
      case 'num': {
        return e.value;
      }

      case 'var': {
        if (!vars.includes(e.name)) return null;
        coeffMap.set(e.name, (coeffMap.get(e.name) ?? Fraction.ZERO).add(Fraction.ONE));
        return Fraction.ZERO;
      }

      case 'add': {
        let sum = Fraction.ZERO;
        for (const child of e.children) {
          const contrib = collect(child);
          if (contrib === null) return null;
          sum = sum.add(contrib);
        }
        return sum;
      }

      case 'mul': {
        if (e.children.length === 0) {
          return Fraction.ONE;
        }
        if (e.children.length === 1) {
          return collect(e.children[0]!);
        }
        if (e.children.length === 2) {
          const child0 = e.children[0]!;
          const child1 = e.children[1]!;

          if (child0.kind === 'num' && child1.kind === 'num') {
            return child0.value.mul(child1.value);
          }

          if (child0.kind === 'num' && child1.kind === 'var') {
            if (!vars.includes(child1.name)) return null;
            coeffMap.set(
              child1.name,
              (coeffMap.get(child1.name) ?? Fraction.ZERO).add(child0.value),
            );
            return Fraction.ZERO;
          }

          if (child0.kind === 'var' && child1.kind === 'num') {
            if (!vars.includes(child0.name)) return null;
            coeffMap.set(
              child0.name,
              (coeffMap.get(child0.name) ?? Fraction.ZERO).add(child1.value),
            );
            return Fraction.ZERO;
          }

          return null;
        }

        return null;
      }

      default: {
        return null;
      }
    }
  }

  const constContrib = collect(expr);
  if (constContrib === null) return null;
  constant = constContrib;

  const coeffs: Fraction[] = Array.from(
    { length: vars.length },
    (_, i) => coeffMap.get(vars[i]!) ?? Fraction.ZERO,
  );

  return { coeffs, constant };
}

// ============================================================================
// Pure Helper Functions (exported and tested)
// ============================================================================

/**
 * Parse a system of equations from structured input.
 */
export function parseSystemInput(
  equations: readonly { lhs: string; rhs: string }[],
  vars: readonly string[],
): Equation[] {
  const varSet = new Set(vars);
  return equations.map((eq) => ({
    lhs: parse(eq.lhs, { variableSet: varSet }),
    rhs: parse(eq.rhs, { variableSet: varSet }),
  }));
}

/**
 * Convert a preset's equation strings into standard-form coefficient cells:
 * one row per equation, [a1 … an, b] as Fraction strings.
 */
export function presetToCells(preset: SystemPreset): string[][] {
  const eqs = parseSystemInput(preset.equations, preset.vars);
  return eqs.map((eq) => {
    const lhs = extractLinearCoefficients(eq.lhs, preset.vars)!;
    const rhs = extractLinearCoefficients(eq.rhs, preset.vars)!;
    const row = preset.vars.map((_, i) =>
      (lhs.coeffs[i] ?? Fraction.ZERO).sub(rhs.coeffs[i] ?? Fraction.ZERO).toString(),
    );
    row.push(rhs.constant.sub(lhs.constant).toString());
    return row;
  });
}

/**
 * Build Equations from coefficient cells. Row count decides the variable set:
 * 2 rows → x,y; 3 rows → x,y,z.
 */
export function cellsToEquations(cells: readonly (readonly string[])[]): {
  eqs: Equation[];
  vars: string[];
} {
  const vars = cells.length === 3 ? ['x', 'y', 'z'] : ['x', 'y'];
  const eqs = cells.map((row) => {
    const terms: Expr[] = [];
    for (let i = 0; i < vars.length; i += 1) {
      const coeff = Fraction.parse(row[i]!);
      if (coeff.num !== 0) {
        terms.push(mul(num(coeff), variable(vars[i]!)));
      }
    }
    const lhs =
      terms.length === 0 ? num(Fraction.ZERO) : terms.length === 1 ? terms[0]! : add(...terms);
    return { lhs: canonicalise(lhs), rhs: num(Fraction.parse(row[vars.length]!)) };
  });
  return { eqs, vars };
}

/** Numeric plane coefficients for the 3D view (row [a, b, c, d] → a·x + b·y + c·z = d). */
export function cellsToPlanes(cells: readonly (readonly string[])[]): Plane3[] {
  return cells.map((row) => {
    const nums = row.map((cell) => Fraction.parse(cell).toNumber());
    return { a: nums[0]!, b: nums[1]!, c: nums[2]!, d: nums[3]! };
  });
}

/** Format one side pair as "lhs = rhs" through the shared formatter. */
export function equationLine(eq: Equation): string {
  const opts = { useUnicodeSuperscripts: true };
  return `${format(canonicalise(eq.lhs), opts)} = ${format(canonicalise(eq.rhs), opts)}`;
}

export interface SystemGraph2DLine {
  a: number;
  b: number;
  c: number;
  className: string;
}

export interface SystemGraph2DOptions {
  domains: CartesianDomain;
  plotRect: CartesianPlotRect;
  lines: readonly SystemGraph2DLine[];
  solution?: { x: number; y: number } | null;
}

export interface SystemGraph2DHandle {
  readonly group: SVGGElement;
  update(options: SystemGraph2DOptions): void;
  destroy(): void;
}

let systemGraphClipId = 0;

function graphTick(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Object.is(rounded, -0) ? '0' : rounded.toString();
}

export function createSystemGraph2D(
  parent: SVGSVGElement | SVGGElement,
  options: SystemGraph2DOptions,
): SystemGraph2DHandle {
  const group = createSvgEl('g', { 'data-role': 'system-graph-2d' });
  parent.appendChild(group);
  let current = options;

  function render(): void {
    group.replaceChildren();
    const { domains, plotRect, lines, solution } = current;
    const [xMin, xMax] = domains.xDomain;
    const [yMin, yMax] = domains.yDomain;
    const mapX = (x: number): number => plotRect.x + ((x - xMin) / (xMax - xMin)) * plotRect.width;
    const mapY = (y: number): number =>
      plotRect.y + (1 - (y - yMin) / (yMax - yMin)) * plotRect.height;

    const defs = createSvgEl('defs');
    const clipId = `alg-system-graph-clip-${systemGraphClipId++}`;
    const clipPath = createSvgEl('clipPath', { id: clipId });
    clipPath.appendChild(
      createSvgEl('rect', {
        x: plotRect.x,
        y: plotRect.y,
        width: plotRect.width,
        height: plotRect.height,
      }),
    );
    defs.appendChild(clipPath);
    const content = createSvgEl('g', { 'clip-path': `url(#${clipId})` });
    const xStep = niceTickStep(xMax - xMin, 6);
    const yStep = niceTickStep(yMax - yMin, 6);
    const axisLayout = getCartesianAxisLayout(domains.xDomain, domains.yDomain);
    const axisY = mapY(axisLayout.xAxis.value);
    const axisX = mapX(axisLayout.yAxis.value);

    for (let index = Math.ceil(xMin / xStep); index * xStep <= xMax + xStep * 1e-9; index += 1) {
      const value = index * xStep;
      const x = mapX(value);
      content.appendChild(
        createSvgEl('line', {
          x1: x,
          y1: plotRect.y,
          x2: x,
          y2: plotRect.y + plotRect.height,
          class: 'alg-gridline',
        }),
      );
      if (Math.abs(value) <= xStep * 1e-9) continue;
      const label = createSvgEl('text', {
        x,
        y: axisY + axisLayout.xAxis.labelDirection * 14,
        'text-anchor': boundaryTextAnchor(x, plotRect.x, plotRect.x + plotRect.width),
        class: 'alg-text-mute',
        'data-axis': 'x',
      });
      label.textContent = graphTick(value);
      content.appendChild(label);
    }

    for (let index = Math.ceil(yMin / yStep); index * yStep <= yMax + yStep * 1e-9; index += 1) {
      const value = index * yStep;
      const y = mapY(value);
      content.appendChild(
        createSvgEl('line', {
          x1: plotRect.x,
          y1: y,
          x2: plotRect.x + plotRect.width,
          y2: y,
          class: 'alg-gridline',
        }),
      );
      if (Math.abs(value) <= yStep * 1e-9) continue;
      const label = createSvgEl('text', {
        x: axisX + axisLayout.yAxis.labelDirection * 8,
        y: y + 4,
        'text-anchor': axisLayout.yAxis.labelAnchor,
        class: 'alg-text-mute',
        'data-axis': 'y',
      });
      label.textContent = graphTick(value);
      content.appendChild(label);
    }

    content.append(
      createSvgEl('line', {
        x1: plotRect.x,
        y1: axisY,
        x2: plotRect.x + plotRect.width,
        y2: axisY,
        class: 'alg-axis',
      }),
      createSvgEl('line', {
        x1: axisX,
        y1: plotRect.y,
        x2: axisX,
        y2: plotRect.y + plotRect.height,
        class: 'alg-axis',
      }),
    );

    for (const line of lines) {
      let x1 = xMin;
      let x2 = xMax;
      let y1: number;
      let y2: number;
      if (line.b !== 0) {
        y1 = (line.c - line.a * x1) / line.b;
        y2 = (line.c - line.a * x2) / line.b;
      } else if (line.a !== 0) {
        x1 = line.c / line.a;
        x2 = x1;
        y1 = yMin;
        y2 = yMax;
      } else {
        continue;
      }
      content.appendChild(
        createSvgEl('line', {
          x1: mapX(x1),
          y1: mapY(y1),
          x2: mapX(x2),
          y2: mapY(y2),
          class: line.className,
          'data-role': 'system-equation',
        }),
      );
    }

    if (solution) {
      const cx = mapX(solution.x);
      const cy = mapY(solution.y);
      content.append(
        createSvgEl('circle', { cx, cy, r: 12, class: 'alg-point', 'fill-opacity': 0.2 }),
        createSvgEl('circle', {
          cx,
          cy,
          r: 6,
          class: 'alg-point',
          'data-role': 'system-solution',
        }),
      );
    }
    group.append(defs, content);
  }

  render();
  return {
    group,
    update(next) {
      current = next;
      render();
    },
    destroy() {
      group.remove();
    },
  };
}

function fitSystemGraphDomains(
  lines: readonly SystemGraph2DLine[],
  solution: { x: number; y: number } | null,
): CartesianDomain {
  const candidates = [6];
  for (const line of lines) {
    if (line.a !== 0) candidates.push(Math.abs(line.c / line.a) + 1);
    if (line.b !== 0) candidates.push(Math.abs(line.c / line.b) + 1);
  }
  if (solution) candidates.push(Math.abs(solution.x) + 1, Math.abs(solution.y) + 1);
  const extent = Math.max(...candidates.filter(Number.isFinite));
  return { xDomain: [-extent, extent], yDomain: [-extent, extent] };
}

/** Build one Equation from a row [coeffs…, rhs], exactly like cellsToEquations builds a row. */
function rowToEquationLine(row: readonly Fraction[], vars: readonly string[]): string {
  const terms: Expr[] = [];
  for (let i = 0; i < vars.length; i += 1) {
    const coeff = row[i]!;
    if (coeff.num !== 0) {
      terms.push(mul(num(coeff), variable(vars[i]!)));
    }
  }
  const lhs =
    terms.length === 0 ? num(Fraction.ZERO) : terms.length === 1 ? terms[0]! : add(...terms);
  return equationLine({ lhs: canonicalise(lhs), rhs: num(row[vars.length]!) });
}

export interface StepPart {
  text: string;
  eqIndex?: number; // 0-based equation index → rendered as a tspan with class alg-eq-{i+1}
}

const DOT = '●';

/**
 * One StepPart[] per solver step: a coloured whole equation for substitution steps;
 * coloured dots naming the row(s) involved plus the coloured resulting equation for row ops.
 * No R-notation anywhere — rows are referred to by colour, not by name.
 */
export function stepParts(result: SystemResult, vars: readonly string[]): StepPart[][] {
  const steps: StepPart[][] = [];
  for (const step of result.steps) {
    switch (step.rule) {
      case 'substitute': {
        steps.push([{ text: equationLine(step.result), eqIndex: step.into }]);
        break;
      }
      case 'row-swap': {
        steps.push([
          { text: 'swap ' },
          { text: DOT, eqIndex: step.a },
          { text: ' and ' },
          { text: DOT, eqIndex: step.b },
        ]);
        break;
      }
      case 'row-scale': {
        steps.push([
          { text: DOT, eqIndex: step.row },
          { text: ` × ${step.factor.toString()}  →  ` },
          { text: rowToEquationLine(step.resultRow, vars), eqIndex: step.row },
        ]);
        break;
      }
      case 'row-add': {
        const sign = step.factor.num < 0 ? '−' : '+';
        const fAbs = step.factor.num < 0 ? step.factor.neg() : step.factor;
        steps.push([
          { text: DOT, eqIndex: step.targetRow },
          { text: ` ${sign} ${fAbs.toString()}·` },
          { text: DOT, eqIndex: step.sourceRow },
          { text: '  →  ' },
          { text: rowToEquationLine(step.resultRow, vars), eqIndex: step.targetRow },
        ]);
        break;
      }
    }
  }
  return steps;
}

// ============================================================================
// State
// ============================================================================

interface State {
  presetId: string;
  advancedMode: boolean;
  systemMode: '2x2' | '3x3';
  method: 'substitution' | 'gaussian';
  cells: string[][];
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): CustomExperimentSpec<State, AlgebraKey, AlgebraContext> {
  return {
    labelKey: 'experiment.systems-laboratory.label',
    demoId: 'algebra',
    advancedKey: 'systems-laboratory',

    initialState(ctx): State {
      return {
        presetId: SYSTEM_PRESETS[0]!.id,
        advancedMode: ctx.advanced,
        systemMode: '2x2',
        method: 'substitution',
        cells: presetToCells(SYSTEM_PRESETS[0]!),
      };
    },

    applyPreset(state, presetId): State {
      const preset = SYSTEM_PRESETS.find((p) => p.id === presetId);
      if (!preset) return state;
      return {
        ...state,
        presetId: preset.id,
        cells: presetToCells(preset),
        systemMode: preset.vars.length === 2 ? '2x2' : '3x3',
        method: preset.vars.length === 3 ? 'gaussian' : state.method,
      };
    },

    createRender(ctx, host, controls) {
      // ── DOM: SVG stage ──
      const svg = createSvgEl('svg', {
        viewBox: '0 0 800 460',
        class: 'alg-systems-laboratory-svg',
      });
      host.appendChild(svg);
      const legend = createFormulaLegend(host);
      legend.element.classList.add('alg-formula-legend--systems');
      let resetView = (): void => {};

      // ── DOM: Panel ──

      // System mode select (2x2 / 3x3)
      const modeRow = document.createElement('div');
      modeRow.className = 'alg-panel-row alg-dropdown-row';
      const modeLabel = document.createElement('label');
      modeLabel.textContent = 'System size:';
      const modeSelect = document.createElement('select');
      modeSelect.className = 'alg-select';
      const opt2x2 = document.createElement('option');
      opt2x2.value = '2x2';
      opt2x2.textContent = '2×2';
      const opt3x3 = document.createElement('option');
      opt3x3.value = '3x3';
      opt3x3.textContent = '3×3';
      modeSelect.appendChild(opt2x2);
      modeSelect.appendChild(opt3x3);
      modeSelect.addEventListener('change', (e) => {
        const target = e.currentTarget as HTMLSelectElement;
        const systemMode: '2x2' | '3x3' = target.value === '2x2' ? '2x2' : '3x3';
        const preset = SYSTEM_PRESETS.find(
          (p) => p.vars.length === (systemMode === '2x2' ? 2 : 3),
        )!;
        controls.setState((s) => ({
          ...s,
          systemMode,
          presetId: preset.id,
          cells: presetToCells(preset),
          method: systemMode === '3x3' ? 'gaussian' : s.method,
        }));
      });
      modeRow.appendChild(modeLabel);
      modeRow.appendChild(modeSelect);
      ctx.panel.appendChild(modeRow);

      // Method select
      const methodRow = document.createElement('div');
      methodRow.className = 'alg-panel-row alg-dropdown-row';
      const methodLabel = document.createElement('label');
      methodLabel.textContent = 'Method:';
      const methodSelect = document.createElement('select');
      methodSelect.className = 'alg-select';
      const optSub = document.createElement('option');
      optSub.value = 'substitution';
      optSub.textContent = ctx.i18n.t('method.substitution');
      const optGauss = document.createElement('option');
      optGauss.value = 'gaussian';
      optGauss.textContent = ctx.i18n.t('method.elimination');
      methodSelect.appendChild(optSub);
      methodSelect.appendChild(optGauss);
      methodSelect.addEventListener('change', (e) => {
        const target = e.currentTarget as HTMLSelectElement;
        const method = target.value as 'substitution' | 'gaussian';
        controls.setState((s) => ({ ...s, method }));
      });
      methodRow.appendChild(methodLabel);
      methodRow.appendChild(methodSelect);
      ctx.panel.appendChild(methodRow);

      // Advanced toggle
      const ADVANCED_KEY = 'algebra.systems-laboratory.advanced';
      const advancedToggle = createToggleRow({
        labelKey: 'btn.advanced',
        i18n: ctx.i18n,
        checked: ctx.advanced,
        onChange: (v) => {
          safeStorage.set(ADVANCED_KEY, v ? 'true' : 'false');
          controls.setState((s) => ({ ...s, advancedMode: v }));
        },
      });
      ctx.panel.appendChild(advancedToggle.element);

      // Coefficient editor (Advanced)
      const coeffGrid = document.createElement('div');
      coeffGrid.className = 'alg-coeff-grid';
      ctx.panel.appendChild(coeffGrid);

      // ── DOM: Action bar ──
      const actionBar = mountActionBar(ctx.actionBar, ctx.i18n);

      // ── Closure vars for render lifecycle ──
      let matrixHandle: ReturnType<typeof createAugmentedMatrix> | null = null;
      let system3dHandle: System3DHandle | null = null;
      let systemGraph2dHandle: SystemGraph2DHandle | null = null;
      let systemGraph2dOptions: SystemGraph2DOptions | null = null;
      let viewport: CartesianViewportHandle | null = null;
      let visibleDomains: CartesianDomain = { xDomain: [-6, 6], yDomain: [-6, 6] };
      let last2dPresetId: string | null = null;
      let view3d: View3D = { ...DEFAULT_VIEW };
      let actionsMounted = false;
      let lastGridKey = '';
      let lastMethodSystemMode: '2x2' | '3x3' | null = null;

      // Helper: convert equation to standard form coefficients
      const getEquationCoefficients = (
        eq: Equation,
        vars: readonly string[],
      ): { coeffs: Fraction[]; constant: Fraction } | null => {
        const lhsCoeffs = extractLinearCoefficients(eq.lhs, vars);
        const rhsCoeffs = extractLinearCoefficients(eq.rhs, vars);
        if (!lhsCoeffs || !rhsCoeffs) return null;

        const coeffs: Fraction[] = [];
        for (let i = 0; i < vars.length; i += 1) {
          coeffs.push(
            (lhsCoeffs.coeffs[i] ?? Fraction.ZERO).sub(rhsCoeffs.coeffs[i] ?? Fraction.ZERO),
          );
        }
        const constant = rhsCoeffs.constant.sub(lhsCoeffs.constant);
        return { coeffs, constant };
      };

      const graphLinesFor = (eqs: readonly Equation[], vars: readonly string[]) =>
        eqs.flatMap((equation, index): SystemGraph2DLine[] => {
          const coefficients = getEquationCoefficients(equation, vars);
          if (!coefficients || coefficients.coeffs.length < 2) return [];
          return [
            {
              a: coefficients.coeffs[0]!.toNumber(),
              b: coefficients.coeffs[1]!.toNumber(),
              c: coefficients.constant.toNumber(),
              className: `alg-curve-${index + 1}`,
            },
          ];
        });

      const graphSolutionFor = (result: SystemResult): { x: number; y: number } | null => {
        if (result.solution.kind !== 'unique') return null;
        const x = result.solution.solutions.get('x');
        const y = result.solution.solutions.get('y');
        if (x?.kind !== 'num' || y?.kind !== 'num') return null;
        return { x: x.value.toNumber(), y: y.value.toNumber() };
      };

      // Render 2×2: graph on left, algebra on right
      const render2x2 = (
        parent: SVGSVGElement,
        eqs: Equation[],
        vars: readonly string[],
        result: SystemResult,
        cells: readonly (readonly string[])[],
      ): void => {
        const lineData = graphLinesFor(eqs, vars);
        const solutionPoint = graphSolutionFor(result);
        systemGraph2dOptions = {
          domains: visibleDomains,
          plotRect: { x: 40, y: 40, width: 360, height: 360 },
          lines: lineData,
          solution: solutionPoint,
        };
        systemGraph2dHandle = createSystemGraph2D(parent, systemGraph2dOptions);

        const resultGroup = createSvgEl('g');
        parent.appendChild(resultGroup);

        const currentSolution = result.solution;

        // Right pane: system, solution, and steps
        const rightX = 440;
        let yOffset = 25;

        const sysTitle = createSvgEl('text', { x: rightX, y: yOffset, class: 'alg-title' });
        sysTitle.textContent = ctx.i18n.t('label.system');
        resultGroup.appendChild(sysTitle);
        yOffset += 22;
        eqs.forEach((eq, i) => {
          const eqText = createSvgEl('text', {
            x: rightX,
            y: yOffset,
            class: `alg-text alg-eq-${i + 1}`,
          });
          eqText.textContent = equationLine(eq);
          resultGroup.appendChild(eqText);
          yOffset += 18;
        });

        yOffset += 14;
        const matrixRows: Fraction[][] = cells.map((row) =>
          row.map((cell) => Fraction.parse(cell)),
        );
        const matGroup = createSvgEl('g');
        parent.appendChild(matGroup);
        matGroup.setAttribute('transform', `translate(${rightX}, ${yOffset})`);
        if (matrixHandle) {
          matrixHandle.destroy();
          matrixHandle = null;
        }
        matrixHandle = createAugmentedMatrix(matGroup, {
          matrix: matrixRows,
          augmentColumn: vars.length,
          width: 240,
          height: 60,
        });
        yOffset += 84;

        const solTitle = createSvgEl('text', { x: rightX, y: yOffset, class: 'alg-title' });
        solTitle.textContent = ctx.i18n.t('label.solution');
        resultGroup.appendChild(solTitle);
        yOffset += 22;

        if (currentSolution.kind === 'unique') {
          for (const [varName, expr] of currentSolution.solutions) {
            const solText = createSvgEl('text', {
              x: rightX.toString(),
              y: yOffset.toString(),
              class: 'alg-result',
            });
            solText.textContent = `${varName} = ${format(canonicalise(expr))}`;
            resultGroup.appendChild(solText);
            yOffset += 18;
          }
        } else if (currentSolution.kind === 'inconsistent') {
          const solText = createSvgEl('text', {
            x: rightX.toString(),
            y: yOffset.toString(),
            class: 'alg-result',
          });
          solText.textContent = ctx.i18n.t('systems.no-solution');
          resultGroup.appendChild(solText);
          yOffset += 18;
        } else {
          const solText = createSvgEl('text', {
            x: rightX.toString(),
            y: yOffset.toString(),
            class: 'alg-result',
          });
          solText.textContent = ctx.i18n.t('systems.infinitely-many');
          resultGroup.appendChild(solText);
          yOffset += 18;

          for (const [varName, expr] of currentSolution.parameterisation) {
            const paramText = createSvgEl('text', {
              x: rightX.toString(),
              y: yOffset.toString(),
              class: 'alg-result',
            });
            paramText.textContent = `${varName} = ${format(canonicalise(expr))}`;
            resultGroup.appendChild(paramText);
            yOffset += 18;
          }
        }

        yOffset += 14;
        const stepsTitle = createSvgEl('text', { x: rightX, y: yOffset, class: 'alg-title' });
        stepsTitle.textContent = ctx.i18n.t('label.steps');
        resultGroup.appendChild(stepsTitle);
        yOffset += 20;

        const parts = stepParts(result, vars);
        const MAX_STEPS = 8;
        parts.slice(0, MAX_STEPS).forEach((stepPartsLine, i) => {
          const stepText = createSvgEl('text', { x: rightX, y: yOffset, class: 'alg-steps' });
          const prefix = createSvgEl('tspan');
          prefix.textContent = `${i + 1}. `;
          stepText.appendChild(prefix);
          for (const part of stepPartsLine) {
            const tspan = createSvgEl(
              'tspan',
              part.eqIndex !== undefined ? { class: `alg-eq-${part.eqIndex + 1}` } : {},
            );
            tspan.textContent = part.text;
            stepText.appendChild(tspan);
          }
          resultGroup.appendChild(stepText);
          yOffset += 16;
        });
        if (parts.length > MAX_STEPS) {
          const moreText = createSvgEl('text', { x: rightX, y: yOffset, class: 'alg-text-mute' });
          moreText.textContent = `+${parts.length - MAX_STEPS} more`;
          resultGroup.appendChild(moreText);
        }
      };

      // Render 3×3: 3D plane view on left, system + matrix + solution + steps on right
      const render3x3 = (
        parent: SVGSVGElement,
        eqs: Equation[],
        vars: readonly string[],
        result: SystemResult,
        cells: readonly (readonly string[])[],
      ): void => {
        const resultGroup = createSvgEl('g');
        parent.appendChild(resultGroup);

        // Left half: 3D plane view
        const threeDGroup = createSvgEl('g');
        parent.appendChild(threeDGroup);
        threeDGroup.setAttribute('transform', 'translate(10, 30)');
        let uniquePoint: [number, number, number] | null = null;
        if (result.solution.kind === 'unique') {
          const coords = ['x', 'y', 'z'].map((v) => {
            const e =
              result.solution.kind === 'unique' ? result.solution.solutions.get(v) : undefined;
            return e && e.kind === 'num' ? e.value.toNumber() : null;
          });
          if (!coords.some((c) => c === null)) {
            uniquePoint = coords as [number, number, number];
          }
        }
        if (system3dHandle) {
          system3dHandle.destroy();
          system3dHandle = null;
        }
        system3dHandle = createSystem3D(threeDGroup, {
          planes: cellsToPlanes(cells),
          point: uniquePoint,
          half: 6,
          width: 400,
          height: 400,
          view: view3d,
          onViewChange: (v) => {
            view3d = v;
          },
        });

        // Right column: system, augmented matrix, solution, steps
        const rightX = 440;
        let yOffset = 25;

        const sysTitle = createSvgEl('text', { x: rightX, y: yOffset, class: 'alg-title' });
        sysTitle.textContent = ctx.i18n.t('label.system');
        resultGroup.appendChild(sysTitle);
        yOffset += 22;
        eqs.forEach((eq, i) => {
          const eqText = createSvgEl('text', {
            x: rightX,
            y: yOffset,
            class: `alg-text alg-eq-${i + 1}`,
          });
          eqText.textContent = equationLine(eq);
          resultGroup.appendChild(eqText);
          yOffset += 18;
        });

        yOffset += 10;
        const matrixRows: Fraction[][] = cells.map((row) =>
          row.map((cell) => Fraction.parse(cell)),
        );
        const matGroup = createSvgEl('g');
        parent.appendChild(matGroup);
        matGroup.setAttribute('transform', `translate(${rightX}, ${yOffset})`);
        if (matrixHandle) {
          matrixHandle.destroy();
          matrixHandle = null;
        }
        matrixHandle = createAugmentedMatrix(matGroup, {
          matrix: matrixRows,
          augmentColumn: vars.length,
          width: 240,
          height: 90,
        });
        yOffset += 114;

        const solTitle = createSvgEl('text', { x: rightX, y: yOffset, class: 'alg-title' });
        solTitle.textContent = ctx.i18n.t('label.solution');
        resultGroup.appendChild(solTitle);
        yOffset += 22;

        const currentSolution = result.solution;
        if (currentSolution.kind === 'unique') {
          for (const [varName, expr] of currentSolution.solutions) {
            const solText = createSvgEl('text', {
              x: rightX.toString(),
              y: yOffset.toString(),
              class: 'alg-result',
            });
            solText.textContent = `${varName} = ${format(canonicalise(expr))}`;
            resultGroup.appendChild(solText);
            yOffset += 18;
          }
        } else if (currentSolution.kind === 'inconsistent') {
          const solText = createSvgEl('text', {
            x: rightX.toString(),
            y: yOffset.toString(),
            class: 'alg-result',
          });
          solText.textContent = ctx.i18n.t('systems.no-solution');
          resultGroup.appendChild(solText);
          yOffset += 18;
        } else {
          const solText = createSvgEl('text', {
            x: rightX.toString(),
            y: yOffset.toString(),
            class: 'alg-result',
          });
          solText.textContent = ctx.i18n.t('systems.infinitely-many');
          resultGroup.appendChild(solText);
          yOffset += 18;

          for (const [varName, expr] of currentSolution.parameterisation) {
            const paramText = createSvgEl('text', {
              x: rightX.toString(),
              y: yOffset.toString(),
              class: 'alg-result',
            });
            paramText.textContent = `${varName} = ${format(canonicalise(expr))}`;
            resultGroup.appendChild(paramText);
            yOffset += 18;
          }
        }

        yOffset += 10;
        const stepsTitle = createSvgEl('text', { x: rightX, y: yOffset, class: 'alg-title' });
        stepsTitle.textContent = ctx.i18n.t('label.steps');
        resultGroup.appendChild(stepsTitle);
        yOffset += 20;

        const parts = stepParts(result, vars);
        const MAX_STEPS = 8;
        parts.slice(0, MAX_STEPS).forEach((stepPartsLine, i) => {
          const stepText = createSvgEl('text', { x: rightX, y: yOffset, class: 'alg-steps' });
          const prefix = createSvgEl('tspan');
          prefix.textContent = `${i + 1}. `;
          stepText.appendChild(prefix);
          for (const part of stepPartsLine) {
            const tspan = createSvgEl(
              'tspan',
              part.eqIndex !== undefined ? { class: `alg-eq-${part.eqIndex + 1}` } : {},
            );
            tspan.textContent = part.text;
            stepText.appendChild(tspan);
          }
          resultGroup.appendChild(stepText);
          yOffset += 15;
        });
        if (parts.length > MAX_STEPS) {
          const moreText = createSvgEl('text', { x: rightX, y: yOffset, class: 'alg-text-mute' });
          moreText.textContent = `+${parts.length - MAX_STEPS} more`;
          resultGroup.appendChild(moreText);
        }
      };

      // ── RenderHandle ──
      const update = (state: State): void => {
        const { advancedMode, systemMode, method } = state;

        // Sync select values from state
        modeSelect.value = systemMode;
        if (systemMode !== lastMethodSystemMode) {
          lastMethodSystemMode = systemMode;
          if (systemMode === '3x3') {
            if (methodSelect.contains(optSub)) optSub.remove();
          } else if (!methodSelect.contains(optSub)) {
            methodSelect.insertBefore(optSub, optGauss);
          }
        }
        methodSelect.value = method;

        // --- Coefficient editor: rebuild on shape/visibility change ---
        const gridKey = `${advancedMode}:${state.cells.length}`;
        if (gridKey !== lastGridKey) {
          lastGridKey = gridKey;
          coeffGrid.replaceChildren();
          coeffGrid.style.display = advancedMode ? '' : 'none';
          if (advancedMode) {
            const gridVars = state.cells.length === 3 ? ['x', 'y', 'z'] : ['x', 'y'];
            state.cells.forEach((row, r) => {
              const rowEl = document.createElement('div');
              rowEl.className = 'alg-coeff-row';
              row.forEach((_, cIdx) => {
                const input = document.createElement('input');
                input.type = 'text';
                input.className = 'alg-text-input alg-coeff-input';
                input.dataset['row'] = r.toString();
                input.dataset['col'] = cIdx.toString();
                const commit = (): void => {
                  const inputs = Array.from(
                    coeffGrid.querySelectorAll<HTMLInputElement>('input.alg-coeff-input'),
                  );
                  try {
                    for (const el of inputs) Fraction.parse(el.value);
                  } catch {
                    return; // any unparseable cell → leave state untouched
                  }
                  controls.setState((s) => {
                    const cells = s.cells.map((cr) => [...cr]);
                    for (const el of inputs) {
                      cells[Number(el.dataset['row'])]![Number(el.dataset['col'])] = el.value;
                    }
                    return { ...s, cells };
                  });
                };
                input.addEventListener('input', commit);
                input.addEventListener('change', commit);
                rowEl.appendChild(input);
                const sep = document.createElement('span');
                sep.textContent =
                  cIdx < gridVars.length - 1
                    ? `${gridVars[cIdx]!} +`
                    : cIdx === gridVars.length - 1
                      ? `${gridVars[cIdx]!} =`
                      : '';
                if (sep.textContent) rowEl.appendChild(sep);
              });
              coeffGrid.appendChild(rowEl);
            });
          }
        }
        // Sync values (guarded, preserves caret)
        if (advancedMode) {
          for (const el of coeffGrid.querySelectorAll<HTMLInputElement>('input.alg-coeff-input')) {
            const v = state.cells[Number(el.dataset['row'])]![Number(el.dataset['col'])]!;
            if (el.value !== v) el.value = v;
          }
        }

        // --- Action bar: mount once ---
        if (!actionsMounted) {
          actionsMounted = true;

          actionBar.setActions([
            {
              id: 'reset',
              labelKey: 'btn.reset',
              onClick: () => {
                controls.setState((s) => {
                  const preset = SYSTEM_PRESETS.find((p) => p.id === s.presetId)!;
                  const nextMode: '2x2' | '3x3' = preset.vars.length === 2 ? '2x2' : '3x3';
                  return {
                    ...s,
                    systemMode: nextMode,
                    cells: presetToCells(preset),
                    method: nextMode === '2x2' ? 'substitution' : 'gaussian',
                  };
                });
              },
            },
            {
              id: 'view-reset',
              labelKey: 'btn.reset-view',
              onClick: () => resetView(),
            },
            {
              id: 'randomise',
              labelKey: 'btn.randomise',
              onClick: () => {
                const idx = Math.floor(ctx.prng() * SYSTEM_PRESETS.length);
                const preset = SYSTEM_PRESETS[idx]!;
                const nextMode: '2x2' | '3x3' = preset.vars.length === 2 ? '2x2' : '3x3';
                controls.setState((s) => ({
                  ...s,
                  presetId: preset.id,
                  systemMode: nextMode,
                  cells: presetToCells(preset),
                  method: nextMode === '2x2' ? s.method : 'gaussian',
                }));
              },
            },
          ]);
        }

        // --- Solve ---
        const { eqs: parsedEqs, vars } = cellsToEquations(state.cells);
        const legendColors = ['var(--accent)', 'var(--alg-x2)', 'var(--alg-x)'];
        const result =
          systemMode === '2x2'
            ? solveSystem(parsedEqs, vars, { method })
            : solveSystem(parsedEqs, vars, { method: 'gaussian' });
        const lineData = graphLinesFor(parsedEqs, vars);
        const solutionPoint = graphSolutionFor(result);

        if (systemMode === '2x2') {
          const base = fitSystemGraphDomains(lineData, solutionPoint);
          if (!viewport) {
            visibleDomains = base;
            viewport = createCartesianViewport(svg, {
              base,
              plotRect: { x: 40, y: 40, width: 360, height: 360 },
              touchActionTarget: '[data-role="system-graph-2d"]',
              onChange(next) {
                visibleDomains = next;
                if (systemGraph2dHandle && systemGraph2dOptions) {
                  systemGraph2dOptions = { ...systemGraph2dOptions, domains: visibleDomains };
                  systemGraph2dHandle.update(systemGraph2dOptions);
                }
              },
            });
          } else if (state.presetId !== last2dPresetId) {
            visibleDomains = base;
            viewport.setBase(base, { notify: false });
          }
          last2dPresetId = state.presetId;
          resetView = () => viewport?.reset();
          legend.setEntries(
            parsedEqs.map((equation, index) => ({
              id: `system-equation-${index}`,
              formula: equationLine(equation),
              color: legendColors[index]!,
            })),
          );
        } else {
          viewport?.dispose();
          viewport = null;
          resetView = (): void => {};
          last2dPresetId = null;
          legend.setEntries([]);
        }

        // --- Re-render SVG (clear + rebuild) ---
        // Destroy matrixHandle/system3dHandle before clearing SVG (handles live inside svg).
        // Both 2x2 and 3x3 own a matrix, so matrixHandle is always destroyed here; only 3x3
        // owns the 3D plane view, so system3dHandle is only torn down when leaving that mode.
        if (matrixHandle) {
          matrixHandle.destroy();
          matrixHandle = null;
        }
        if (systemGraph2dHandle) {
          systemGraph2dHandle.destroy();
          systemGraph2dHandle = null;
          systemGraph2dOptions = null;
        }
        if (systemMode !== '3x3') {
          if (system3dHandle) {
            system3dHandle.destroy();
            system3dHandle = null;
          }
        }
        Array.from(svg.children).forEach((child) => {
          if (!child.hasAttribute('data-cartesian-viewport-surface')) child.remove();
        });

        if (systemMode === '2x2') {
          render2x2(svg, parsedEqs, vars, result, state.cells);
        } else {
          render3x3(svg, parsedEqs, vars, result, state.cells);
        }
      };

      return {
        update,
        dispose() {
          viewport?.dispose();
          matrixHandle?.destroy();
          system3dHandle?.destroy();
          systemGraph2dHandle?.destroy();
          legend.destroy();
          actionBar.clear();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
